import { BackLink, Card, Column, Columns, KeyValues, Loading, Notice, PersonHeader, Pill, Pills, PrivacyNote, sentenceCase } from '@trellistry/ui';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError, api } from '../api.js';

interface PersonCard {
  person: { id: string; displayName: string; given: string; surname: string; nameCn: string | null; pinyin: string | null; knownAs: string | null; status: string; membershipType: string | null; gender: string | null; dateOfBirth: string | null; localeMessages: string };
  contacts: { id: string; kind: string; label: string | null; phone: string | null; email: string | null; address: { line1: string; line2: string | null; postalCode: string | null } | null; preferred: boolean }[];
  household: { id: string; name: string; members: { personId: string; displayName: string; knownAs: string | null; role: string }[] } | null;
  connections: { personId: string; displayName: string; knownAs: string | null; type: string; label: string }[];
  consent: { channel: string; status: string; stopReceivedAt: string | null }[];
}

const roleWords: Record<string, string> = { head: 'Head of household', spouse: 'Spouse', child: 'Child', parent: 'Parent', member: 'Member' };
const channelWords: Record<string, string> = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email', post: 'Post', phone: 'Phone' };

const backToPeople = <BackLink as={Link} to="/people">People</BackLink>;

/** Someone else on this card, as a link to their own. */
function PersonLink({ person, current }: { person: { personId: string; displayName: string; knownAs: string | null }; current?: string }) {
  return (
    <>
      {person.personId === current ? <strong>{person.displayName}</strong> : <Link to={`/people/${person.personId}`}>{person.displayName}</Link>}
      {person.knownAs && <span className="muted"> · {person.knownAs}</span>}
    </>
  );
}

/** The person's card (GLOSSARY: never "profile page"). Opening it is recorded: we record who looked. */
export function PersonCardPage() {
  const { id } = useParams();
  const [card, setCard] = useState<PersonCard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCard(null);
    setError(null);
    api<PersonCard>(`/people/${id}`).then(setCard).catch((e: unknown) => setError(e instanceof ApiError && e.status === 404 ? 'We couldn’t find that person.' : 'We couldn’t load this card just now.'));
  }, [id]);

  if (error) return <>{backToPeople}<Notice tone="error">{error}</Notice></>;
  if (!card) return <Loading>Loading…</Loading>;
  const p = card.person;
  const meta = [p.membershipType && sentenceCase(p.membershipType), card.household?.name, `Messages in ${p.localeMessages === 'zh-SG' ? '中文' : 'English'}`].filter(Boolean).join(' · ');

  return (
    <>
      {backToPeople}
      <PersonHeader name={p.displayName} nameCn={p.nameCn} pinyin={p.pinyin} knownAs={p.knownAs} meta={meta}>
        {p.status !== 'active' && <Pills><Pill tone="attention">{sentenceCase(p.status)}</Pill></Pills>}
      </PersonHeader>

      <Columns>
        <Column>
          <Card title="Contact">
            {card.contacts.length === 0 ? <p className="muted">No contact details yet.</p> : (
              <KeyValues items={card.contacts.map((c) => ({
                key: c.id,
                label: <>{sentenceCase(c.label ?? c.kind)}{c.preferred && <><span aria-hidden="true"> ★</span><span className="visually-hidden"> (preferred)</span></>}</>,
                value: c.phone ?? c.email ?? (c.address ? [c.address.line1, c.address.line2, c.address.postalCode && `Singapore ${c.address.postalCode}`].filter(Boolean).join(', ') : ''),
              }))} />
            )}
          </Card>
          <Card title={card.household ? `Household · ${card.household.name}` : 'Household'}>
            {!card.household ? <p className="muted">Not in a household yet.</p> : (
              <KeyValues items={card.household.members.map((m) => ({ key: m.personId, label: roleWords[m.role] ?? m.role, value: <PersonLink person={m} current={p.id} /> }))} />
            )}
          </Card>
          <Card title="Connections">
            {card.connections.length === 0 ? <p className="muted">No connections recorded beyond the household.</p> : (
              <KeyValues items={card.connections.map((c) => ({ key: `${c.personId}-${c.type}`, label: c.label, value: <PersonLink person={c} /> }))} />
            )}
          </Card>
        </Column>
        <Column>
          <Card title="Permission to contact" tone="muted">
            {card.consent.length === 0 ? <p className="muted">Not recorded yet, so church messages won’t go to this person.</p> : (
              <Pills>
                {card.consent.map((c) => (
                  <Pill key={c.channel} tone={c.stopReceivedAt ? 'error' : c.status === 'granted' ? 'success' : 'neutral'}>{channelWords[c.channel] ?? c.channel}: {c.stopReceivedAt ? 'asked us to stop' : c.status}</Pill>
                ))}
              </Pills>
            )}
          </Card>
          <p className="privacy-line"><PrivacyNote>Opening this card was recorded. We record who looked.</PrivacyNote></p>
        </Column>
      </Columns>
    </>
  );
}
